import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projects } from '../data/projects';
import { useLanguage } from '../context/LanguageContext';

export default function ProjectDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    
    // Find the project data
    const project = projects[language]?.find(p => p.id.toString() === id);

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!project) {
        return (
            <main className="relative w-full min-h-screen flex flex-col justify-center items-center z-10 grid-sys max-w-[1920px] mx-auto border-x border-border">
                <div className="col-span-12 text-center flex flex-col items-center gap-8">
                    <h1 className="text-4xl font-heading uppercase text-accent">PROJECT ARCHIVE NOT FOUND</h1>
                    <button onClick={() => navigate(-1)} className="font-mono text-xs uppercase tracking-widest text-muted hover:text-foreground">RETURN TO BASE</button>
                </div>
            </main>
        );
    }

    return (
        <main className="relative w-full min-h-screen flex flex-col z-10 grid-sys max-w-[1920px] mx-auto border-x border-border pt-32 pb-32">
            
            {/* Header Control Panel */}
            <div className="col-span-12 flex justify-between items-center p-6 border-b-brut md:px-12">
                <button onClick={() => navigate(-1)} className="flex items-center gap-4 group">
                    <div className="w-8 h-8 rounded-full border border-border flex items-center justify-center group-hover:bg-foreground group-hover:border-foreground transition-colors">
                        <svg className="w-4 h-4 text-foreground group-hover:text-background transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted group-hover:text-foreground transition-colors">RETURN TO VAULT</span>
                </button>
                <div className="flex gap-8">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted hidden md:block">SPECIFICATION NO. {project.id.toString().padStart(3, '0')}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-accent font-bold">STATUS: {project.details.status}</span>
                </div>
            </div>

            {/* Massive Title Area */}
            <div className="col-span-12 p-6 md:p-12 border-b-brut bg-secondary">
                 <motion.h1 
                    initial={{ opacity: 0, y: "20%" }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="text-6xl md:text-[12vw] font-heading uppercase text-foreground tracking-[0.02em] leading-[0.85] overflow-visible"
                >
                     {project.title}
                 </motion.h1>
            </div>

            {/* Main Image Banner */}
            <div className="col-span-12 border-b-brut bg-secondary h-[40vh] md:h-[60vh] overflow-hidden">
                <motion.img 
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    src={project.img} 
                    alt={project.title} 
                    className="w-full h-full object-cover filter contrast-125 saturate-50 hover:saturate-100 transition-all duration-1000"
                />
            </div>

            {/* Specification Grid Content */}
            <div className="col-span-12 grid grid-cols-1 md:grid-cols-12">
                
                {/* Meta Panel */}
                <div className="col-span-12 md:col-span-4 p-6 md:p-12 border-b-brut md:border-b-0 md:border-r-brut flex flex-col gap-12 bg-background">
                    <div className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] text-muted uppercase tracking-widest">CATEGORY</span>
                        <span className="font-mono text-sm uppercase text-foreground font-bold">{project.category}</span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <span className="font-mono text-[10px] text-muted uppercase tracking-widest">YEAR OF INCEPTION</span>
                        <span className="font-mono text-sm uppercase text-foreground font-bold">{project.year || "2024"}</span>
                    </div>
                    {project.link && (
                        <div className="flex flex-col gap-4 mt-auto">
                            <span className="font-mono text-[10px] text-muted uppercase tracking-widest">DEPLOYMENT</span>
                            <a href={project.link} target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
                                <span className="font-mono text-xs uppercase tracking-widest text-foreground group-hover:text-accent transition-colors">INITIALIZE PROJECT</span>
                                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </div>
                            </a>
                        </div>
                    )}
                </div>

                {/* Content Panel */}
                <div className="col-span-12 md:col-span-8 p-6 md:p-12 flex flex-col gap-16 bg-background">
                    
                    {/* Mission Data */}
                    <div className="flex flex-col gap-6 max-w-2xl">
                        <div className="flex items-center gap-4">
                            <div className="w-2 h-2 bg-accent" />
                            <h3 className="font-mono text-xs uppercase tracking-widest text-accent">PRIMARY OBJECTIVE // THE MISSION</h3>
                        </div>
                        <p className="font-mono text-sm md:text-base text-foreground leading-relaxed uppercase opacity-90">
                            {project.details.mission}
                        </p>
                    </div>

                    <div className="w-full h-[1px] bg-border" />

                    {/* Execution Data */}
                    <div className="flex flex-col gap-6 max-w-2xl">
                        <div className="flex items-center gap-4">
                            <div className="w-2 h-2 bg-foreground" />
                            <h3 className="font-mono text-xs uppercase tracking-widest text-muted">TECHNICAL DELIVERY // THE EXECUTION</h3>
                        </div>
                        <p className="font-mono text-sm md:text-base text-muted leading-relaxed uppercase">
                            {project.details.execution}
                        </p>
                    </div>

                </div>

            </div>

        </main>
    );
}
